import React, {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  makeStyles,
  Divider as DividerMUI,
  Drawer,
  Typography as TypographyMUI,
} from '@material-ui/core';
import { Popover } from '@mui/material';
import {
  Close,
  FormatListBulleted,
  MoreVert,
  Search,
  TextFields,
  ArrowBack,
} from '@material-ui/icons';

import { SCREEN_SIZE_CATEGORIES } from 'constants';
import {
  ContentsTabs,
  SearchMenu,
  HelpMenu,
  AppearanceSettingsMenu,
  KeyboardShortcutsMenu,
  IconButton,
  IconTooltip,
} from 'components';
import OfflineMenu from 'components/OfflineMenu';
import PrintMenu from 'components/PrintMenu';
import { CancelDownloadConfirm } from 'components/Offline';
import { CollaborationMenu } from 'components/Collaboration';
import {
  AnalyticsModifiersContext,
  BookLocationValuesContext,
  BookLocationModifiersContext,
  DocumentValuesContext,
  OfflineValuesContext,
  ViewportValuesContext,
  UserContentItemModifiersContext,
} from 'contexts';
import { AdditionalMenus, additionalMenusArray } from 'constants';

const menuArr = ['contents', 'search', 'appearance', 'additional'];
const menuObj = {
  contents: {
    id: 'contents',
    title: 'Contents',
    icon: <FormatListBulleted />,
  },
  search: {
    id: 'search',
    title: 'Search',
    icon: <Search />,
  },
  appearance: {
    id: 'appearance-settings',
    title: 'Appearance Settings',
    icon: <TextFields />,
  },
  // To be added after Phase 1
  // viewing: {
  //   id: 'viewing-options',
  //   title: 'Viewing Options',
  //   icon: <Visibility />,
  // },
  additional: {
    id: 'additional-options',
    title: 'Additional Options',
    icon: <MoreVert />,
  },
};

const useStyles = makeStyles((theme) => {
  return {
    /* Styles applied to the root element. */
    root: {
      display: 'flex',
    },
    popover: {
      overflow: 'hidden', // prevent double scrollbars on menus with scrollable menuContent
      maxHeight: 'calc(100% - 110px)',
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.greyBlue[700],
      ...theme.typography.body2,
      gap: theme.spacing(0.5),
    },
    titleContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: `${theme.spacing(1)}px ${theme.spacing(1)}px ${theme.spacing(
        1,
      )}px ${theme.spacing(2)}px`, // 8px 8px 8px 16px
    },
    titleContainerBack: {
      padding: theme.spacing(1),
    },
    menuContainer: ({ activeMenu }) => ({
      width: theme.spacing(50),
      ...theme.typography.body2,

      [theme.breakpoints.down('sm')]: {
        width: 'auto',
        overflowY: activeMenu === AdditionalMenus.Offline ? 'hidden' : 'auto',
      },
    }),
    additionalOptionsMenus: {
      maxHeight: 'calc(100vh - 170px)', // stop just above the scrub bar (title + topbar + scrubbar)

      [theme.breakpoints.down('sm')]: {
        height: 'calc(100vh - 62px)',
        maxHeight: 'calc(100vh - 62px)', // vh minus title bar height
      },
    },
    searchMenu: {
      maxHeight: 'calc(100vh - 170px)', // stop just above the scrub bar (title + topbar + scrubbar)

      [theme.breakpoints.down('sm')]: {
        maxHeight: 'calc(100vh - 62px)', // vh minus title bar height
      },
    },
    appearanceMenuDrawer: {
      '&.MuiPaper-root': {
        '&.MuiDrawer-paperAnchorBottom': {
          maxHeight: '60%',
        },
      },
    },
    defaultDrawer: {
      '&.MuiPaper-root': {
        '&.MuiDrawer-paperAnchorBottom': {
          height: '100%',
        },
      },
    },
  };
});

const BackButton = forwardRef(({ onBack }, ref) => {
  return (
    <IconButton ref={ref} aria-label="Back" onClick={onBack}>
      <ArrowBack />
    </IconButton>
  );
});

const MenuButton = ({ id, icon, menu, isSelected, handleClick }) => {
  return (
    <IconTooltip id={`${id}-menu-popover`} title={menu}>
      <IconButton
        aria-label={menu}
        isPressed={isSelected}
        onClick={(evt) => {
          handleClick(menu);
        }}
      >
        {icon}
      </IconButton>
    </IconTooltip>
  );
};

const toCamelCase = (string) => {
  if (!string) {
    return 'unknown';
  }

  return string
    .split(' ')
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
};

const MenuBar = ({
  className,
  children,
  bookLocation,
  setAnnounceLocationChangeMessage,
  ...props
}) => {
  const analytics = useContext(AnalyticsModifiersContext);

  const { goToLocationCalledAt } = useContext(BookLocationValuesContext);
  const { frame } = useContext(DocumentValuesContext);
  const { setShouldFocusFrame } = useContext(BookLocationModifiersContext);

  const { viewport } = useContext(ViewportValuesContext);
  const { isAccessOfflineContentDialogOpen } = useContext(OfflineValuesContext);
  const { onDialogClose, onDialogOpen } = useContext(
    UserContentItemModifiersContext,
  );

  const shouldUseDrawer =
    viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.TABLET;

  const divRef = useRef(null);
  const backButtonRef = useRef(null);
  const titleRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const [activeMenu, setActiveMenu] = useState();
  const [lastActiveMenu, setlastActiveMenu] = useState(null);
  const [lastAdditionalMenuOpened, setLastAdditionalMenuOpened] =
    useState(null);

  const [cancelDownloadDialogProps, setCancelDownloadDialogProps] = useState({
    isOpen: false,
    marker: null,
  });

  const classes = useStyles({ activeMenu });

  const callAnalyticsEvent = useCallback(
    (menuClicked) => {
      if (!menuClicked) {
        return;
      }
      switch (menuClicked) {
        case 'Contents':
          analytics.tableOfContentsMenuOpen();
          break;
        case 'Search':
          analytics.searchMenuOpen();
          break;
        case AdditionalMenus.Collaboration:
          analytics.collaborationMenuOpen();
          break;
        case AdditionalMenus.Offline:
          analytics.downloadToOfflineMenuOpen();
          break;
        case AdditionalMenus.Print:
          analytics.printToPdfMenuOpen();
          break;
        default:
          return;
      }
    },
    [analytics],
  );

  const handleClick = useCallback(
    (value) => {
      setlastActiveMenu(null);
      setAnchorEl(divRef.current);
      setActiveMenu(value);
      callAnalyticsEvent(value);
      onDialogOpen(`${toCamelCase(value)}Dialog`);
    },
    [callAnalyticsEvent, onDialogOpen],
  );

  const handleClose = useCallback(
    (retainFocus = true) => {
      Boolean(retainFocus)
        ? setlastActiveMenu(activeMenu)
        : setlastActiveMenu(null);
      setAnchorEl(null);
      setActiveMenu(null);
      setLastAdditionalMenuOpened(null);
      onDialogClose(`${toCamelCase(activeMenu)}Dialog`);
    },
    [activeMenu, onDialogClose],
  );

  const handleAdditionalMenusClick = useCallback(
    (value) => {
      onDialogClose('additionalOptionsDialog');
      handleClick(value);
    },
    [onDialogClose, handleClick],
  );

  const handleCloseAndAnnounceLocation = useCallback(
    (bookLocation) => {
      setAnnounceLocationChangeMessage(null);

      handleClose(false);
      setShouldFocusFrame(true);

      setTimeout(() => {
        setAnnounceLocationChangeMessage(
          `Navigated to ${bookLocation.readableLocation}`,
        );
      }, 2000);
    },
    [handleClose, setAnnounceLocationChangeMessage, setShouldFocusFrame],
  );

  const goToLocationCalledAtRef = useRef(goToLocationCalledAt);

  useEffect(
    function closeMobileDrawersOnLocationChange() {
      if (goToLocationCalledAt === goToLocationCalledAtRef.current) {
        // Already attempted to close for this navigation timestamp
        // Return to avoid closing automatically upon opening a menu (which updates handleClose)
        return;
      }

      if (shouldUseDrawer) {
        handleClose();
      }

      goToLocationCalledAtRef.current = goToLocationCalledAt;
    },
    [goToLocationCalledAt, handleClose, shouldUseDrawer],
  );

  const getMenuObjKey = (menuToFind) => {
    return Object.keys(menuObj).find((key) => {
      return additionalMenusArray.includes(menuToFind)
        ? menuObj[key].title === 'Additional Options'
        : menuObj[key].title === menuToFind;
    });
  };
  // retain focus on the last open menu item on closing
  useEffect(() => {
    if (lastActiveMenu) {
      divRef.current.children[
        menuArr.indexOf(getMenuObjKey(lastActiveMenu))
      ].focus({
        focusVisible: true,
      });
    }

    if (!lastActiveMenu && !activeMenu) {
      frame?.focus();
    }
  }, [lastActiveMenu, activeMenu, frame]);

  const drawerClass =
    activeMenu === 'Appearance Settings'
      ? classes.appearanceMenuDrawer
      : classes.defaultDrawer;

  const open = Boolean(anchorEl);
  const menuId = open
    ? `${menuObj[getMenuObjKey(activeMenu)].id}-menu-popover`
    : undefined;

  const closeButtonRef = useRef(null);
  useEffect(() => {
    if (open && closeButtonRef.current) {
      setTimeout(() => {
        closeButtonRef.current.focus(); // Focus the close button when dialog or popover opens
      }, 100);
    }
  }, [open]);

  const CloseButton = forwardRef(({ onClose }, ref) => {
    return (
      <IconButton ref={ref} aria-label="Close" onClick={onClose}>
        <Close />
      </IconButton>
    );
  });

  const menuContentsJsx = {
    contentsMenu: <ContentsTabs onClose={handleCloseAndAnnounceLocation} />,
    searchMenu: (
      <>
        <DividerMUI aria-label="" />
        <SearchMenu
          className={classes.searchMenu}
          onClose={handleCloseAndAnnounceLocation}
        />
      </>
    ),
    additionalOptionsMenu: (
      <>
        <DividerMUI aria-label="" />
        <HelpMenu
          className={classes.additionalOptionsMenus}
          onOfflineClick={() =>
            handleAdditionalMenusClick(AdditionalMenus.Offline)
          }
          onPrintClick={() => handleAdditionalMenusClick(AdditionalMenus.Print)}
          onShortcutsClick={() =>
            handleAdditionalMenusClick(AdditionalMenus.Keyboard)
          }
          onCollaborationClick={() =>
            handleAdditionalMenusClick(AdditionalMenus.Collaboration)
          }
          lastAdditionalMenuOpened={lastAdditionalMenuOpened}
        />
      </>
    ),
    appearanceSettingsMenu: bookLocation ? (
      <>
        <DividerMUI aria-label="" />
        <AppearanceSettingsMenu
          isFixedWidth={Boolean(bookLocation.document.fixedWidth)}
        />
      </>
    ) : null,
    keyboardShortcutsMenu: (
      <>
        <DividerMUI aria-label="" />
        <KeyboardShortcutsMenu className={classes.additionalOptionsMenus} />
      </>
    ),
    offlineMenu: (
      <>
        <DividerMUI aria-label="" />
        <OfflineMenu
          backButtonRef={backButtonRef}
          className={classes.additionalOptionsMenus}
          onClose={handleCloseAndAnnounceLocation}
          setCancelDownloadDialogProps={setCancelDownloadDialogProps}
          titleRef={titleRef}
        />
      </>
    ),
    printMenu: (
      <>
        <DividerMUI aria-label="" />
        <PrintMenu
          onClose={handleClose}
          className={classes.additionalOptionsMenus}
        />
      </>
    ),
    collaborationMenu: (
      <>
        <CollaborationMenu onClose={handleClose} />
      </>
    ),
  };

  const openAdditionalOptionsMenu = () => {
    setLastAdditionalMenuOpened(activeMenu);
    handleClick('Additional Options');
    onDialogClose(`${toCamelCase(activeMenu)}Dialog`);
  };

  // All menus combined and set to a const because it is used twice in the render call
  const menuContent = (
    <>
      <div
        className={clsx(
          classes.titleContainer,
          additionalMenusArray.includes(activeMenu) &&
            classes.titleContainerBack,
        )}
      >
        <div ref={titleRef} className={classes.title}>
          {additionalMenusArray.includes(activeMenu) && (
            <BackButton
              ref={backButtonRef}
              onBack={openAdditionalOptionsMenu}
            />
          )}
          <TypographyMUI component="h2" variant="h5" id="menu-dialog-title">
            {activeMenu}
          </TypographyMUI>
        </div>
        <CloseButton
          ref={closeButtonRef}
          className={classes.closeButton}
          onClose={handleClose}
        />
      </div>
      <div className={classes.menuContainer}>
        {(() => {
          switch (activeMenu) {
            case 'Contents':
              return menuContentsJsx.contentsMenu;
            case 'Search':
              return menuContentsJsx.searchMenu;
            case 'Additional Options':
              return menuContentsJsx.additionalOptionsMenu;
            case 'Appearance Settings':
              return menuContentsJsx.appearanceSettingsMenu;
            case AdditionalMenus.Keyboard:
              return menuContentsJsx.keyboardShortcutsMenu;
            case AdditionalMenus.Offline:
              return menuContentsJsx.offlineMenu;
            case AdditionalMenus.Print:
              return menuContentsJsx.printMenu;
            case AdditionalMenus.Collaboration:
              return menuContentsJsx.collaborationMenu;
            default:
              return <div>{children}</div>;
          }
        })()}
      </div>
    </>
  );

  return (
    <>
      <div ref={divRef} className={clsx(classes.root, className)} {...props}>
        {menuArr.map((menu) => (
          <MenuButton
            id={menuObj[menu].id}
            key={menuObj[menu].id}
            icon={menuObj[menu].icon}
            menu={menuObj[menu].title}
            isSelected={activeMenu === menuObj[menu].title}
            handleClick={handleClick}
          />
        ))}
        {shouldUseDrawer ? (
          <Drawer
            disableAutoFocus={false} // Allow focus to move into drawer
            disableEnforceFocus={
              cancelDownloadDialogProps.isOpen ||
              isAccessOfflineContentDialogOpen
            }
            anchor="bottom"
            classes={{ paper: drawerClass }}
            id={menuId}
            ModalProps={{
              keepMounted: false, // Unmount when closed to reset focus state
            }}
            open={open}
            PaperProps={{
              role: 'dialog',
              'aria-labelledby': 'menu-dialog-title',
              'aria-label': activeMenu,
              'aria-modal': 'true',
              tabIndex: -1,
            }}
          >
            {menuContent}
          </Drawer>
        ) : (
          <Popover
            disableAutoFocus={false} // Allow focus to move into popover
            // fixes maximum call stack exceeded error
            // https://github.com/mui/material-ui/issues/10341
            disableEnforceFocus={
              cancelDownloadDialogProps.isOpen ||
              isAccessOfflineContentDialogOpen
            }
            disableRestoreFocus={false} // Restore focus on close
            anchorEl={anchorEl}
            anchorOrigin={{
              horizontal: 'left',
              vertical: 'bottom',
            }}
            classes={{ paper: classes.popover }}
            id={menuId}
            onClose={handleClose}
            open={open}
            PaperProps={{
              role: 'dialog',
              'aria-labelledby': 'menu-dialog-title',
              'aria-label': activeMenu,
              'aria-modal': 'true',
              tabIndex: -1,
            }}
            transformOrigin={{
              horizontal: 'left',
              vertical: 'top',
            }}
          >
            {menuContent}
          </Popover>
        )}
      </div>
      {/* Cancel ongoing download */}
      <CancelDownloadConfirm
        cancelDownloadDialogProps={cancelDownloadDialogProps}
        setCancelDownloadDialogProps={setCancelDownloadDialogProps}
      />
    </>
  );
};

MenuBar.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  bookLocation: PropTypes.object,
  setAnnounceLocationChangeMessage: PropTypes.func,
};

export default memo(MenuBar);
